import React, { useState } from "react";
import { ModalX } from "../../../components";
import { Button, Checkbox, Input, Option, Select, Textarea } from "@material-tailwind/react";
import { AddEngagement, UpdateEngagement } from "../../../services/backend";
import { convertToPresentableText, Notify, NotifyError } from "../../../utils/functions";
import { CONTACT_TYPES_ENUM, DEAL_STAGES } from "../../../utils/enums";

// A `datetime-local` input only accepts "YYYY-MM-DDTHH:mm" in LOCAL time. Anything else
// (an ISO string, a bare date) is rejected and the field silently renders empty — which is
// what used to happen to the "default to now" value here.
const toDateTimeLocalValue = (value) => {
  const date = value ? new Date(value) : new Date();
  if (isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const buildFormData = (engagement) => ({
  date: toDateTimeLocalValue(engagement?.date),
  contactMethod: engagement?.contactMethod || '',
  notes: engagement?.notes || '',
  outcome: engagement?.outcome || '',
  followUpRequired: engagement?.followUpRequired || false
});

const EngagementFormModal = ({ isOpen, onClose, leadId, leadStatus, engagement, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const isEditing = !!engagement?._id;
  // A won deal has no callback left to schedule, so the flag is not offered on WON leads
  // (the backend refuses to store it for them either).
  const isWon = leadStatus === DEAL_STAGES.WON;
  const [formData, setFormData] = useState(buildFormData(null));

  // Reset form when modal opens — prefilled with the engagement being edited, if any
  React.useEffect(() => {
    if (isOpen) {
      const data = buildFormData(engagement);
      setFormData(isWon ? { ...data, followUpRequired: false } : data);
    }
  }, [isOpen, engagement, isWon]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBoxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.contactMethod) {
      Notify('Please select a contact method', 'error');
      return;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await UpdateEngagement(leadId, engagement._id, formData);
        Notify('Follow-up updated successfully', 'success');
      } else {
        await AddEngagement(leadId, formData);
        Notify('Engagement added successfully', 'success');
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving engagement', error);
      NotifyError(isEditing ? 'Failed to update follow-up' : 'Failed to add engagement', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalX
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit Follow-up" : "Add Follow-up"}
      maxWidth="xl"
    >
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="flex gap-4 flex-col md:flex-row">
            <Input
              label="Date"
              name="date"
              type="datetime-local"
              value={formData.date}
              onChange={handleInputChange}
              required
            />
            <Select
              label="Contact Method"
              value={formData.contactMethod}
              onChange={(value) => setFormData({ ...formData, contactMethod: value })}
              required
            >
              {CONTACT_TYPES_ENUM.map((method, index) => (
                <Option key={index} value={method}>
                  {convertToPresentableText(method)}
                </Option>
              ))}
            </Select>
          </div>

          <Textarea
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
          />

          <Textarea
            label="Outcome"
            name="outcome"
            value={formData.outcome}
            onChange={handleInputChange}
            required
          />

          {!isWon && (
            <Checkbox
              name="followUpRequired"
              checked={formData.followUpRequired}
              onChange={handleCheckBoxChange}
              label="Follow-up Required"
            />
          )}
        </div>

        <hr className="my-4" />

        <div className="flex justify-end space-x-2">
          <Button
            onClick={onClose}
            className="rounded-full"
            variant="text"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="rounded-full"
            disabled={loading}
          >
            {isEditing ? 'Save Changes' : 'Add Engagement'}
          </Button>
        </div>
      </form>
    </ModalX>
  );
};

export default EngagementFormModal;